import { v4 as uuid } from 'uuid';
import {
  ContentSchema,
  RoomJSONStructureSchema,
  RoomJSONStructureItem,
  ContentsSchema,
  RoomStructureHeader,
  RoomStructureContent,
  RoomStructureFolder,
  SalesActionSchema,
  SectionHeaderSchema,
} from '../../definitions/schemas';
import ContentType, { isContentNotMentionable } from '../../enums/ContentType';
import { conditionalAssignment } from '../Object';
import {
  getContentCover,
  getTextContentURL,
} from '../../components/Content/helper';
import { isEmbeddableSpreadsheet } from '../Document';
import {
  RoomContentTypes,
  RoomStructureItemType,
} from '../../enums/RoomStructureItemType';
import { StorageProviderMTPickers } from '../../enums/Integrations/StorageProviders';
import OrganizationsStore from '../../stores/observables/OrganizationsStore';
import { getContentName } from '../../components/Room/components/Content/helper';
import { Mention as MentionPillType } from '../../components/Room/components/BuyerCopilotChats/MentionsNode/MentionPill';
import {
  Breadcrumb,
  FolderChangeEntry,
  FolderReference,
} from '../../types/Folder';
/**
 * Apply special conditions to the structure item (like forcing elements visibility)
 *
 * Regarding database updates:
 *   Even though the structure is fixed locally, it won't save the fixes
 *   until the structure item is marked as dirty (apply any change).
 *   Publishing the structure won't change any non-dirty elements.
 *
 * @warning only add conditions that should be run on the
 *          initial load and for every item updates
 */
const applyStructureItemFixes = <T extends RoomJSONStructureItem>(
  content: Partial<ContentSchema>,
  structureItem: T,
  isInlineIframeViewerEnabled: boolean
): T => {
  let fixedStructureItem: T = structureItem;

  /**
   * URL contents should force their name and description to always visible.
   * This is a special condition because the name and description are rendered
   * inside the URL card (not outside at the content level block/box)
   */
  const isAssetHubContent =
    content.mtData?.provider === StorageProviderMTPickers.AssetHub;
  if (
    content.type === ContentType.URL &&
    !(isAssetHubContent && isInlineIframeViewerEnabled)
  ) {
    fixedStructureItem = {
      ...structureItem,
      nameVisible: true,
      descriptionVisible: true,
    };
  }

  if (isAssetHubContent && structureItem.isDownloadable === undefined) {
    const organization = OrganizationsStore.getCurrentOrganization();
    const defaultContentDownload =
      organization?.defaultContentDownload || false;

    fixedStructureItem = {
      ...structureItem,
      isDownloadable: defaultContentDownload,
    };
  }

  return fixedStructureItem;
};

const buildTextBoxContent = (
  id: string,
  text = ''
): Partial<ContentSchema> => ({
  id,
  libraryVisible: false,
  text: { html: true, text, version: 1 },
  type: ContentType.TEXT,
});

export const shouldDisplayInFullWidth = (item: RoomJSONStructureItem) =>
  item.fullWidth || item.type === RoomStructureItemType.Header;

export const isContentTextBox = (content?: Partial<ContentSchema>): boolean => {
  const [, isUrl] = getTextContentURL(content?.text?.text);
  return content?.type === ContentType.TEXT && !isUrl;
};

export const buildStructureItemId = (item: RoomJSONStructureItem): string =>
  `${item.type}-${item.id}`;

/**
 * Merges `additions` into `prev` by id — a shallow field merge, last write
 * wins per field. Used when pooling a folder's contents into
 * `selectedContent` from more than one place — e.g. `useFolderData`'s own
 * fetch and `applyFolderTargetedContents`'s fetch can both resolve for the
 * same folder (the former runs when a folder is opened, the latter when
 * content is targeted at it before it's been fetched) and would otherwise
 * each append the same records, duplicating tiles.
 *
 * A matching id is merged (`{ ...old, ...new }`), not replaced wholesale:
 * the two sources can carry different field sets (e.g. a GraphQL
 * view-optimized selection vs. a full record from content creation), so
 * swapping the whole object would silently drop whichever fields only the
 * "losing" copy had. `additions`' fields win on overlap since they're the
 * data that just arrived and may reflect changes `prev`'s copy doesn't have
 * yet — but fields `additions` doesn't specify are preserved from `prev`.
 */
export const dedupeContentsUnion = (
  prev: ContentsSchema,
  additions: ContentsSchema
): ContentsSchema => {
  const additionsById = new Map<string, Partial<ContentSchema>>();
  additions.forEach((content) => {
    if (content.id) additionsById.set(content.id, content);
  });

  const merged = prev.map((content) =>
    content.id && additionsById.has(content.id)
      ? { ...content, ...additionsById.get(content.id) }
      : content
  );

  const prevIds = new Set(prev.map((content) => content.id));
  const newOnly = Array.from(additionsById.entries())
    .filter(([id]) => !prevIds.has(id))
    .map(([, content]) => content);

  return [...merged, ...newOnly];
};

/**
 * Builds a structure item for a content or header, keeping only the
 * necessary and basic keys for the object, to avoid cluttering the structure.
 * If the content is a single-use text content (that is not a text that only
 * contains a url), it's changed to a text box so it can be handled properly
 * on the room/room template flows
 */
export const buildStructureItem = (
  item: Partial<ContentSchema> | SectionHeaderSchema,
  type: RoomStructureItemType,
  isInlineIframeViewerEnabled = false
): RoomJSONStructureItem => {
  if (type === RoomStructureItemType.Header) {
    const header = item as SectionHeaderSchema;
    return {
      headerVisible: header.headerVisible,
      id: header.id,
      icon: header.icon,
      logo: header.signedUrl,
      name: item.name,
      type,
      uri: header.uri,
    };
  }

  const content = item as ContentSchema;
  const isTextBox = isContentTextBox(content);

  let newType = type;
  if (isTextBox && !content.libraryVisible) {
    newType = RoomStructureItemType.TextBox;
  }

  // Apply fixes everytime a new item is added
  // Moving forward new structure items will store the latest valid key-values
  let structureItem = applyStructureItemFixes(
    content,
    {
      id: content.id,
      type: newType,
      ...conditionalAssignment(isTextBox, {
        fullWidth: true,
        name: content.name,
        description: content.description,
        text: content.text?.text,
        type: newType,
      }),
    } as RoomJSONStructureItem,
    isInlineIframeViewerEnabled
  );

  /**
   * Contents that are references (those coming from provide references)
   * will have their names visible by DEFAULT. It's the content itself
   * which is named after the question's name. Basically, if the name of
   */
  if (content.questionId) {
    structureItem = {
      ...structureItem,
      nameVisible: true,
    };
  }

  return structureItem;
};

/**
 * Checks if there's a header in the structure that matches the given id,
 * so we can avoid repeating headers that are added automatically when
 * a content with a section header is selected and added to the room
 */
export const isHeaderInStructure = (
  structure: RoomJSONStructureSchema | undefined,
  headerId: string
) =>
  !!structure &&
  structure.items.some(
    ({ id, type }) => id === headerId && type === RoomStructureItemType.Header
  );

/**
 * Matches headers and contents with their positions in the room
 * structure. With this, the visual representation of the room can be
 * built, as this is where the structure's ids and config is matched
 * with the proper target object
 *
 * @note Text Boxes are usually considered and treated as contents, as they are
 * basically single-use text contents. But for cloning a room or initializing
 * from a template, there could be text boxes that don't have an existing
 * content record yet, so they have a special treatment by mocking a content for them
 */
export const matchStructureItems = (
  selectedContent: ContentsSchema,
  structure: RoomJSONStructureSchema | undefined,
  isInlineIframeViewerEnabled = false,
  childFolders?: FolderReference[]
): [RoomStructureHeader[], RoomStructureContent[], RoomStructureFolder[]] => {
  const contentItems: RoomStructureContent[] = [];
  const headerItems: RoomStructureHeader[] = [];
  const folderItems: RoomStructureFolder[] = [];

  structure?.items.forEach((item) => {
    const { id, name = '', text, type } = item;
    if (type === RoomStructureItemType.Header) {
      headerItems.push({ ...item, name });
      return;
    }

    if (type === RoomStructureItemType.Folder) {
      const folderItem = item as RoomStructureFolder;
      if (folderItem.id) {
        const childFolder = childFolders?.find((cf) => cf.id === folderItem.id);
        folderItems.push(
          childFolder ? { ...childFolder, ...folderItem } : folderItem
        );
      }
      return;
    }

    const match = selectedContent.find((item) => item.id === id);

    if (match) {
      /**
       * Apply fixes on matching structure and contents
       * Read `applyStructureItemFixes` notes on database updates
       * @warning this method is run on the initial load and after updates
       */
      contentItems.push(
        applyStructureItemFixes(
          match,
          { ...item, match },
          isInlineIframeViewerEnabled
        )
      );
      return;
    }

    /**
     * If no match was found for a TextBox, it means it's a new one that
     * doesn't exist as a content yet and it'll be created once the room or
     * room template is saved. Therefore, a simulated content is used.
     * This could happen if a room is initialized from another room (clone flow)
     * or from a room template that have a text box which must be copied but
     * not overwritten
     */
    if (type === RoomStructureItemType.TextBox) {
      contentItems.push({ ...item, match: buildTextBoxContent(id, text) });
    }
  });

  return [headerItems, contentItems, folderItems];
};

/**
 * Derives a folder's `coverSigned` from the folder's own contents, mirroring
 * the backend's on-load derivation (folder_item_enricher.rb) so the local
 * preview matches what the server will re-derive after save.
 *
 * Rules (kept in lockstep with the backend):
 *  - Looks at the FIRST structure item ONLY (index 0). If it is not a Content
 *    (a header, text box, subfolder, or empty folder), there is no cover — we
 *    do NOT skip ahead to the first content, nor recurse into subfolders.
 *  - Resolves the cover through `getContentCover`, the SAME helper the content
 *    tile uses, so a folder's cover always matches its first content's cover.
 *
 * Intended for callers that have already loaded the folder's contents (i.e. the
 * folder was fetched). For folders that were never opened, the backend-provided
 * `coverSigned` on the folder structure item should be used as-is instead.
 */
export const deriveFolderCoverSigned = (
  folderStructure: RoomJSONStructureItem[],
  contentPool: ContentsSchema
): string => {
  const first = folderStructure[0];
  if (!first || first.type !== RoomStructureItemType.Content) {
    return '';
  }

  const content = contentPool.find((c) => c.id === first.id) ?? {};
  const [coverUrl, defaultCoverUrl, coverFromStructure] = getContentCover(
    content,
    first
  );

  return coverFromStructure || coverUrl || defaultCoverUrl || '';
};

export interface FolderChangesOverrideBindings {
  getFolderChange: (folderId: string) => FolderChangeEntry | undefined;
  selectedContent: ContentsSchema;
}

/**
 * Overrides matched folders' `itemCount`/`coverSigned` from in-memory
 * `folderChanges` edit state, so unsaved local edits (new folders, contents
 * added/removed) are reflected before the room is saved.
 *
 * Only derives itemCount/coverSigned from `entry.structure` once the folder
 * has been fetched (`entry.fetched`) — a rename-only entry is created with
 * structure: [] and would otherwise incorrectly report itemCount=0 for
 * non-empty folders that were never opened locally.
 */
export const applyFolderChangesOverride = (
  structureFolders: RoomStructureFolder[],
  bindings?: FolderChangesOverrideBindings
): RoomStructureFolder[] => {
  if (!bindings) return structureFolders;

  return structureFolders.map((f) => {
    const entry = f.id ? bindings.getFolderChange(f.id) : undefined;
    if (!entry) {
      return f;
    }
    return {
      ...f,
      itemCount: entry.fetched ? entry.structure.length : f.itemCount ?? 0,
      ...(entry.fetched
        ? {
            coverSigned: deriveFolderCoverSigned(
              entry.structure,
              bindings.selectedContent
            ),
          }
        : {}),
    };
  });
};

/**
 * Orders the room's selected sales actions based on the order they have
 * in the room structure, to match the room author's intention. This matches
 * the sales actions object, with the items in the structure, which are
 * basically an id representing the position of the item
 */
export const getOrderedSalesActionsFromStructure = (
  salesActions: SalesActionSchema[],
  structure: RoomJSONStructureSchema | undefined
): SalesActionSchema[] => {
  if (!structure?.buttons) {
    return salesActions;
  }

  const orderedSelected: SalesActionSchema[] = [];
  structure.buttons.forEach(({ id }) => {
    const action = salesActions.find((action) => action.id === id);
    if (action) {
      orderedSelected.push(action);
    }
  });

  return orderedSelected;
};

/**
 * Orders the selected contents based on the order in the structure, as
 * the structure acts as the source of truth to how the room should look
 * like. Having the selected contents and the structure well ordered
 * all the time, makes it easier to update one another, as the
 * order on both affect each other
 */
export const getOrderedContentsFromStructure = (
  contents: ContentsSchema,
  structure: RoomJSONStructureSchema
): ContentsSchema => {
  const newSelectedContent: ContentsSchema = [];

  const orderedStructureItems = structure.items.filter(({ type }) =>
    RoomContentTypes.includes(type)
  );

  orderedStructureItems.forEach(({ id }) => {
    const content = contents.find((content) => content.id === id);
    if (content) {
      newSelectedContent.push(content);
    }
  });

  return newSelectedContent;
};

/**
 * Builds a room structure based on the order of the given contents. This
 * is used to create a room/room template based on some preselected contents
 * from the library. If the `skipHeaders` argument is given, then a header
 * item is not added for the contents' section header, for example: in the
 * create content link flow
 */
export const getStructureFromContents = (
  contents: ContentsSchema,
  sectionHeaders: SectionHeaderSchema[],
  skipHeaders = false,
  isInlineIframeViewerEnabled = false
): RoomJSONStructureSchema => {
  const newStructure: RoomJSONStructureSchema = { items: [] };
  const headersStructure = new Map();

  contents.forEach((content) => {
    const { sectionHeaderId } = content;
    const targetSectionHeaderId = sectionHeaderId || 'none';

    if (sectionHeaderId && !headersStructure.has(sectionHeaderId)) {
      const sectionHeader = sectionHeaders.find(
        ({ id }) => id === sectionHeaderId
      );

      if (sectionHeader) {
        headersStructure.set(sectionHeaderId, [
          buildStructureItem(
            sectionHeader,
            RoomStructureItemType.Header,
            isInlineIframeViewerEnabled
          ),
        ]);
      }
    }

    const items = headersStructure.get(targetSectionHeaderId) || [];
    items.push(
      buildStructureItem(
        content,
        RoomStructureItemType.Content,
        isInlineIframeViewerEnabled
      )
    );
    headersStructure.set(targetSectionHeaderId, items);
  });

  headersStructure.forEach((items) =>
    items.forEach((item: RoomJSONStructureItem) => {
      if (item.type === RoomStructureItemType.Header && skipHeaders) {
        return;
      }
      newStructure.items.push(item);
    })
  );

  return newStructure;
};

/**
 * Gets the name, logo or icon from a room header. The structure is always
 * the source of truth for the section headers, as it's kept updated
 * with any change on the org section headers
 */
export const getHeaderInfo = (
  structureHeader: RoomStructureHeader
): [string, string, string] => {
  const { icon, logo, name } = structureHeader;

  const headerIcon = icon || '';
  const headerLogo = logo || '';

  return [name!, headerIcon, headerLogo];
};

/**
 * Gets the count of documents (non spreadsheets) in a room structure
 *
 * This is mostly used to detect when a room has many documents to enable
 * PDFViewer viewport loading optimizations
 */
export const getDocumentsCount = (structureContents: RoomStructureContent[]) =>
  structureContents.reduce((pV, content) => {
    const {
      match: { document, type },
    } = content;
    return (
      pV +
      (type === ContentType.DOCUMENT &&
      document &&
      !isEmbeddableSpreadsheet(document)
        ? 1
        : 0)
    );
  }, 0);

/**
 * Filters the items in the structure to remove all of them that aren't
 * currently available for the room, for the scenario where a content has
 * been deleted from the library
 */
export const getCurrentItemsInStructure = (
  contents: ContentsSchema,
  structure: RoomJSONStructureSchema
) => {
  const structureItems: RoomJSONStructureItem[] = [];

  structure.items.forEach((item) => {
    // headers always exist because there's no item that matches in room.contents
    // as it only contains actual contents records from the db
    const exists =
      item.type !== RoomStructureItemType.Content ||
      contents.some(({ id }) => id === item.id);

    if (exists) {
      structureItems.push(item);
    }
  });

  return structureItems;
};

export const getStructureItemHiddenFromVisitorsState = (
  item: RoomJSONStructureItem,
  activeSectionHiddenFromVisitors: boolean
): {
  activeSectionHiddenFromVisitors: boolean;
  sectionHiddenFromVisitors: boolean;
} => {
  if (item.type === RoomStructureItemType.Header) {
    const hidden = !(item.headerVisible ?? true);
    return {
      sectionHiddenFromVisitors: hidden,
      activeSectionHiddenFromVisitors: hidden,
    };
  }

  return {
    sectionHiddenFromVisitors:
      (RoomContentTypes.includes(item.type) ||
        item.type === RoomStructureItemType.Folder) &&
      activeSectionHiddenFromVisitors,
    activeSectionHiddenFromVisitors,
  };
};

/**
 * Filters structure items hidden from visitors (hidden section headers and
 * their contents). Mirrors backend structure_items_visible_to_contact.
 */
export const filterStructureItemsHiddenFromVisitors = (
  structureItems: RoomJSONStructureItem[]
): RoomJSONStructureItem[] => {
  let activeSectionHiddenFromVisitors = false;

  return structureItems.filter((item) => {
    const { activeSectionHiddenFromVisitors: next, sectionHiddenFromVisitors } =
      getStructureItemHiddenFromVisitorsState(
        item,
        activeSectionHiddenFromVisitors
      );
    activeSectionHiddenFromVisitors = next;

    if (item.type === RoomStructureItemType.Header) {
      return !sectionHiddenFromVisitors;
    }

    return !activeSectionHiddenFromVisitors;
  });
};

/**
 * Builds a valid RoomJSONStructureItem for a header, using the base
 * information and overwriting it with the changes for that particular base.
 * This is used in 2 scenarios:
 *  - One section header is edited, so the pristine header is received as base
 *    and the changes that were made to it are applied
 *  - One new section header is being created, so the base is received with
 *    a generated id and some config and the rest of the changes are applied
 */
export const buildStructureHeader = (
  base: Partial<RoomStructureHeader>,
  changes: Partial<SectionHeaderSchema>
): RoomJSONStructureItem => {
  const { icon, name, signedUrl, uri } = changes;

  const newHeader = {
    ...base,
    icon,
    logo: signedUrl,
    name,
    type: RoomStructureItemType.Header,
    uri,
  };

  if (newHeader.icon) {
    delete newHeader.logo;
    delete newHeader.uri;
  }

  if (newHeader.logo) {
    delete newHeader.icon;
  }

  return newHeader as RoomJSONStructureItem;
};

/**
 * Builds a structure by removing archived contents from the initial values.
 * This is used for the clone flow, if the original room contains archived
 * contents that need not to be copied over to the clone
 */
export const filterArchivedContentsFromStructure = (
  contents: ContentsSchema,
  structure: RoomJSONStructureSchema
): [ContentsSchema, RoomJSONStructureSchema] => {
  const nonArchivedContents = contents.filter(({ archived }) => !archived);
  const filteredIds = nonArchivedContents.map(({ id }) => id!);

  const newStructureItems = structure?.items.filter(
    (item) =>
      item.type !== RoomStructureItemType.Content ||
      filteredIds.includes(item.id)
  );

  return [nonArchivedContents, { ...structure, items: newStructureItems }];
};

/**
 * Generates a new room structure by copying the text box contents into
 * the structure with new random generated IDs, so they can be treated as
 * brand new text boxes. This implies that new content records will be created
 * for them, instead of creating a new version for the existing ones.
 * This is used for scenarios that require initializing a room based on another
 * room (clone flow) or a template, since the result of those flows is to have
 * independent contents, so users can edit them freely in their rooms, without
 * affecting one another.
 *
 * @note this should ideally be performed by the backend
 */
export const prepareRoomStructureForCopy = (
  structure: RoomJSONStructureSchema,
  contents: ContentsSchema = []
): [RoomJSONStructureSchema, ContentsSchema] => {
  const newStructure: RoomJSONStructureSchema = { ...structure, items: [] };
  const textBoxContentsIds: string[] = [];

  structure.items.forEach((item) => {
    const { id, type } = item;
    if (type === RoomStructureItemType.Header) {
      newStructure.items.push(item);
      return;
    }

    // Folder items are containers, not contents — keep them as-is so they are
    // not misclassified as text boxes by the fallthrough below. The folder tree
    // (including id remapping) is handled separately by prepareRoomFolders.
    if (type === RoomStructureItemType.Folder) {
      newStructure.items.push(item);
      return;
    }

    const content = contents.find((content) => content.id === id);

    // consider single-use text contents as text boxes (retroactively) if
    // they weren't marked as text_box in the structure, this
    const isTextBox = isContentTextBox(content) && !content?.libraryVisible;
    if (type === RoomStructureItemType.Content && !isTextBox) {
      newStructure.items.push(item);
      return;
    }

    const text = content?.text?.text || item.text || '';
    const newType = RoomStructureItemType.TextBox;

    newStructure.items.push({ ...item, id: uuid(), text, type: newType });

    if (content) {
      textBoxContentsIds.push(id);
    }
  });

  const newContents = contents.filter(
    ({ id }) => !textBoxContentsIds.includes(id!)
  );

  return [newStructure, newContents];
};

/**
 * Removing unnecessary fields from the structure items.
 * For section headers: we want to store only the relevant values, so we
 * remove the convenience fields like: url and logo, which contain variable
 * URLs that we don't want to store, as those are computed when the values
 * are fetched
 */
export const buildRoomStructurePayload = (
  structure: RoomJSONStructureSchema
): RoomJSONStructureSchema => ({
  ...structure,
  items: structure.items.map((item) => {
    if (item.type === RoomStructureItemType.Header) {
      const { logo, url, ...headerItem } = item;
      return headerItem;
    }

    if (item.type === RoomStructureItemType.Content) {
      const { coverSigned, ...contentItem } = item;
      return contentItem;
    }

    // Folder cover is derived server-side from the folder's first content, so
    // don't persist any cover fields on a Folder item. We patch `coverSigned`
    // locally for immediate tile re-render, but must strip it (and the other
    // cover fields) from the save payload — mirroring the Content strip above.
    // itemCount/updatedAt/depth are server-computed read enrichment
    // (FolderItemEnricher) that rides along on any folder item fetched via
    // GraphQL — never persist them back (DSR itemCount-leak bug).
    if (item.type === RoomStructureItemType.Folder) {
      const {
        coverSigned,
        coverUri,
        coverAssetId,
        coverType,
        itemCount,
        updatedAt,
        depth,
        ...folderItem
      } = item;
      return folderItem;
    }

    return item;
  }),
});

/**
 * Whether any folder change entry contains at least one actual content item
 * (DSR-4516). Content added inside a folder lives in that folder's own
 * structure (in `folderChanges`), not the room/template root structure, so the
 * root-only content-presence check misses it and leaves Save disabled.
 *
 * Only counts real content items via `RoomContentTypes` — Folder items are
 * excluded, so an EMPTY folder (or a folder containing only sub-folders with no
 * content) does NOT count as having content. Covers nested subfolders too,
 * since each folder is its own entry in the map.
 */
export const folderChangesHaveContent = (
  folderChanges?: Map<string, { structure: RoomJSONStructureItem[] }>
): boolean =>
  !!folderChanges &&
  Array.from(folderChanges.values()).some((entry) =>
    entry.structure.some((item) => RoomContentTypes.includes(item.type))
  );

/**
 * Derives a folder's `parentFolderId` from its breadcrumb trail. The trail is
 * the full chain from the root folder down to (and including) the folder
 * itself, so the parent is the second-to-last crumb. Root folders (trail of
 * length 1, or empty) have no parent. Mirrors the convention used by
 * useRoomContainers.getParentFolderIdFromBreadcrumbs.
 */
const getParentFolderIdFromBreadcrumbs = (
  folderId: string,
  breadcrumbs: Breadcrumb[]
): string | null => {
  if (!breadcrumbs.length) return null;
  const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
  const parentCrumb =
    lastCrumb.id === folderId ? breadcrumbs[breadcrumbs.length - 2] : lastCrumb;
  return parentCrumb?.id ?? null;
};

export const buildFolderChangesPayload = (
  folderChanges: Map<
    string,
    {
      name: string;
      description?: string;
      structure: RoomJSONStructureItem[];
      breadcrumbs?: Breadcrumb[];
      dirty: boolean;
      fetched?: boolean;
    }
  >,
  // When true (template flow), blank out text-box ids so the backend creates
  // fresh text contents on save — mirrors buildRoomTemplateStructurePayload for
  // the root structure. Rooms pass false (default) to keep their ids.
  blankTextBoxIds = false
): any | undefined => {
  const dirtyEntries = Array.from(folderChanges.entries()).filter(
    ([, entry]) => entry.dirty
  );

  if (dirtyEntries.length === 0) {
    return undefined;
  }

  const payload = dirtyEntries.map(([id, entry]) => {
    const base = {
      id,
      name: entry.name,
      description: entry.description,
    };

    // A folder that has never been fetched from the server (e.g. renamed via
    // its tile without ever being opened) has no reliable local view of its
    // structure or parent. Sending `structure`/`parentFolderId` in that case
    // would overwrite the folder's real contents with an empty list and
    // detach it from its parent (DSR folder-wipe bug). Omit both keys so the
    // backend leaves them untouched — Folders::BulkUpdate treats an absent
    // `structure`/`parent_folder_id` key as "no change".
    if (!entry.fetched) {
      return base;
    }

    return {
      ...base,
      // NOTE: folder cover is derived SERVER-SIDE from the folder's first
      // content, so we intentionally do NOT send a folder-level `cover`.
      // Emit parentFolderId so the backend can rebuild the nested folder tree.
      // Without it every folder (including subfolders) looks like a root and
      // the nesting is dropped at save time.
      parentFolderId: getParentFolderIdFromBreadcrumbs(
        id,
        entry.breadcrumbs ?? []
      ),
      structure: {
        // Strip the same server-read enrichment as buildRoomStructurePayload:
        // itemCount/updatedAt/depth are FolderItemEnricher output on nested
        // Folder-type items and must never be persisted back (DSR
        // itemCount-leak bug); cover fields on Folder items are likewise
        // server-derived from the folder's first content.
        items: entry.structure.map((item) => {
          const { coverSigned, itemCount, updatedAt, depth, ...rest } = item;
          if (rest.type === RoomStructureItemType.Folder) {
            const { coverUri, coverAssetId, coverType, ...folderItem } = rest;
            return folderItem;
          }
          if (blankTextBoxIds && rest.type === RoomStructureItemType.TextBox) {
            return { ...rest, id: '' };
          }
          return rest;
        }),
      },
    };
  });

  return payload;
};

/**
 * A folder change entry derived from a prepared (cloned) template folder,
 * ready to be seeded into local `folderChanges` so the cloned tree can be
 * navigated/edited exactly like a manually-created folder.
 */
export interface PreparedFolderChange {
  id: string;
  name: string;
  description?: string;
  structure: RoomJSONStructureItem[];
  breadcrumbs: FolderReference[];
}

/**
 * Result of normalizing the folders returned by `prepareFolders`:
 *  - `folderChanges`: entries to seed into local state (one per cloned folder)
 *  - `contents`: the pooled contents across all cloned folders, to merge into
 *    `selectedContent` so `useFolderData` can resolve folder items locally
 *    without re-fetching.
 *  - `idMap`: maps each source (template) folder id to its newly minted clone
 *    id (keyed by `oldFolderId`), so any template folder id appearing in the
 *    room structure can be rewritten to the new id (mirrors
 *    processStructureWithCopiedEntities for content).
 */
export interface PreparedFoldersResult {
  folderChanges: PreparedFolderChange[];
  contents: ContentsSchema;
  idMap: Map<string, string>;
}

/** Minimal shape consumed from the `prepareFolders` response. */
export interface PreparedFolder {
  id: string;
  /** Source/template folder id this clone was created from (DSR-4752). */
  oldFolderId?: string;
  name: string;
  structure?: RoomJSONStructureSchema;
  contents?: ContentsSchema;
}

/**
 * Maps each source (template) folder id to its newly minted clone id, using the
 * authoritative `oldFolderId` linkage returned per folder by `prepareFolders`.
 *
 * Covers ALL returned folders (roots and nested), so any template folder id
 * that appears in the room structure can be remapped — no positional ordering
 * or root detection required.
 */
const buildFolderIdMap = (
  preparedFolders: PreparedFolder[]
): Map<string, string> => {
  const idMap = new Map<string, string>();

  preparedFolders.forEach((folder) => {
    if (folder.oldFolderId) {
      idMap.set(folder.oldFolderId, folder.id);
    }
  });

  return idMap;
};

/**
 * Rewrites a structure so every Folder item that references a source (template)
 * folder id is replaced with its newly minted clone id. Folder items whose id
 * is not in the map are left untouched.
 *
 * The room structure normally references only root folder ids, but the full
 * `idMap` also covers nested ids, so this safely handles either case.
 *
 * Mirrors processStructureWithCopiedEntities (content id rewrite) for folders.
 */
export const remapStructureFolderIds = (
  structure: RoomJSONStructureSchema,
  idMap: Map<string, string>
): RoomJSONStructureSchema => ({
  ...structure,
  items: (structure.items || []).map((item) => {
    if (item.type !== RoomStructureItemType.Folder || !item.id) {
      return item;
    }
    const newId = idMap.get(item.id);
    return newId ? { ...item, id: newId } : item;
  }),
});

/**
 * Derives a breadcrumb trail for every prepared folder.
 *
 * The `prepareFolders` response does not return breadcrumbs; the nesting is
 * encoded in each folder's `structure` (Folder items reference child folder
 * ids). We reconstruct child -> parent links from those structures and walk up
 * to build each folder's trail, so renaming/navigation behaves identically to
 * manually-created folders.
 */
const buildPreparedFolderBreadcrumbs = (
  preparedFolders: PreparedFolder[]
): Map<string, FolderReference[]> => {
  const nameById = new Map<string, string>();
  const parentById = new Map<string, string>();

  preparedFolders.forEach((folder) => {
    nameById.set(folder.id, folder.name);
    (folder.structure?.items ?? []).forEach((item) => {
      if (item.type === RoomStructureItemType.Folder && item.id) {
        parentById.set(item.id, folder.id);
      }
    });
  });

  const breadcrumbsById = new Map<string, FolderReference[]>();

  preparedFolders.forEach((folder) => {
    const trail: FolderReference[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined = folder.id;

    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      trail.unshift({ id: cursor, name: nameById.get(cursor) ?? '' });
      cursor = parentById.get(cursor);
    }

    breadcrumbsById.set(folder.id, trail);
  });

  return breadcrumbsById;
};

/**
 * Normalizes the folders returned by the `prepareFolders` op into the shape
 * needed to hydrate local state.
 *
 * `prepareFolders` mints NEW folder ids and returns the full nested tree (a
 * flat list, internally self-consistent: nested Folder references inside each
 * `structure` already use the new clone ids). This function therefore:
 *  - seeds one folder change per returned folder, keyed by its new id;
 *  - pools all contents for local resolution;
 *  - returns an `idMap` (source folder id -> new clone id, via each folder's
 *    `oldFolderId`) so the caller can rewrite the room structure via
 *    `remapStructureFolderIds`.
 *
 * When `applyUrlOverrides` is true (vendor mode), URL-content items in each
 * folder's structure get their `url` overwritten with the prepared content's
 * room-specific signed url — mirroring `storeUrlOverrides` for the room-level
 * structure (see Review.storeUrlOverrides / CopyTemplate.prepareStructure).
 */
export const normalizePreparedFolders = (
  preparedFolders: PreparedFolder[],
  applyUrlOverrides = false
): PreparedFoldersResult => {
  const pooledContents: ContentsSchema = [];
  const breadcrumbsById = buildPreparedFolderBreadcrumbs(preparedFolders);
  const idMap = buildFolderIdMap(preparedFolders);

  const folderChanges = preparedFolders.map((folder) => {
    const contents = folder.contents ?? [];
    pooledContents.push(...contents);

    const items = (folder.structure?.items ?? []).map((item) => {
      if (!applyUrlOverrides || item.type !== RoomStructureItemType.Content) {
        return item;
      }

      const content = contents.find((c) => c.id === item.id);
      if (!content || content.type !== ContentType.URL) {
        return item;
      }

      return { ...item, url: content.url?.url };
    });

    return {
      id: folder.id,
      name: folder.name,
      structure: items,
      breadcrumbs: breadcrumbsById.get(folder.id) ?? [],
    };
  });

  return { folderChanges, contents: pooledContents, idMap };
};

/**
 * Options that mirror the room's onContentDetailsChange behavior when a content
 * is EDITED (as opposed to plain attribute tweaks):
 *  - `newId`: forks the item to a new id (the room reassigns edited library text
 *    content to a fresh uuid so the backend creates a NEW version rather than
 *    mutating the original). Callers precompute the uuid so they can keep the
 *    pooled content (selectedContent) id in sync with the structure item.
 *  - `typeOverwrite`: changes the item type (e.g. single-use text -> text_box).
 */
export interface UpdateStructureItemOptions {
  newId?: string;
  typeOverwrite?: RoomStructureItemType;
}

/**
 * Updates a single structure item's display attributes (name, cover, visibility, etc.)
 * within a structure items array. Returns the new array.
 * Reusable for both room-level and folder-level structures.
 */
export const updateStructureItemDetails = (
  items: RoomJSONStructureItem[],
  itemId: string,
  contentAttributes: {
    coverAssetId?: string;
    coverSigned?: string;
    coverType?: string;
    coverUri?: string;
    description?: string | null;
    descriptionVisible?: boolean | null;
    fullWidth?: boolean;
    isDownloadable?: boolean | null;
    name?: string | null;
    nameVisible?: boolean | null;
    text?: string;
    url?: string;
  },
  options?: UpdateStructureItemOptions
): RoomJSONStructureItem[] =>
  items.map((item) => {
    if (item.id !== itemId || item.type === RoomStructureItemType.Header) {
      return item;
    }

    const {
      coverAssetId,
      coverSigned,
      coverType,
      coverUri,
      description = null,
      descriptionVisible = null,
      fullWidth,
      isDownloadable = null,
      name = null,
      nameVisible = null,
      text,
      url,
    } = contentAttributes;

    return {
      ...item,
      ...conditionalAssignment(name !== null, { name }),
      ...conditionalAssignment(nameVisible !== null, { nameVisible }),
      ...conditionalAssignment(description !== null, { description }),
      ...conditionalAssignment(descriptionVisible !== null, {
        descriptionVisible,
      }),
      ...conditionalAssignment(fullWidth !== undefined, { fullWidth }),
      ...conditionalAssignment(isDownloadable !== null, { isDownloadable }),
      ...conditionalAssignment(!!text, { text }),
      ...conditionalAssignment(!!url, { url }),
      // Fork to a new id on edit (matches the room's createCopy -> uuid) so the
      // backend creates a new text-content version instead of mutating the original.
      ...conditionalAssignment(!!options?.newId, { id: options?.newId }),
      ...conditionalAssignment(!!options?.typeOverwrite, {
        type: options?.typeOverwrite,
      }),
      ...conditionalAssignment(!!item.coverUri || !!coverUri, {
        coverUri,
        coverType,
        coverSigned,
      }),
      ...conditionalAssignment(!!item.coverAssetId || !!coverAssetId, {
        coverAssetId,
      }),
    };
  });

/**
 * Removes a content item from a structure items array. Returns the new array.
 * Reusable for both room-level and folder-level structures.
 * RoomContentController's addContent/removeContent handle the room-specific
 * complexity (section headers, content ordering relative to headers, template headers).
 * For folders, we don't have section headers — it's just a flat list
 */
export const removeItemFromStructure = (
  items: RoomJSONStructureItem[],
  itemId: string
): RoomJSONStructureItem[] =>
  items.filter(
    ({ id, type }) => id !== itemId || type === RoomStructureItemType.Header
  );

/**
 * Recursively collects all folder IDs within a folder's structure, including nested subfolders.
 * Used when deleting a folder to clean up all descendant folders.
 */
export const collectFolderIdsRecursively = (
  folderId: string,
  folderChanges: Map<string, { structure: RoomJSONStructureItem[] }>
): string[] => {
  const folderIds = [folderId];
  const folderEntry = folderChanges.get(folderId);

  if (folderEntry?.structure) {
    folderEntry.structure.forEach((item) => {
      if (item.type === RoomStructureItemType.Folder) {
        const childFolderIds = collectFolderIdsRecursively(
          item.id,
          folderChanges
        );
        folderIds.push(...childFolderIds);
      }
    });
  }

  return folderIds;
};

/**
 * Removes a folder and all its children (contents and subfolders) from the structure.
 * Returns the new structure with all descendant items removed.
 */
export const removeFolderAndChildrenFromStructure = (
  items: RoomJSONStructureItem[],
  folderId: string,
  folderChanges: Map<string, { structure: RoomJSONStructureItem[] }>
): RoomJSONStructureItem[] => {
  const folderEntry = folderChanges.get(folderId);

  if (!folderEntry?.structure) {
    // If we don't have folder data, just remove the folder item itself
    return items.filter((item) => item.id !== folderId);
  }

  // Collect all IDs to remove (folder children and nested folders)
  const idsToRemove = new Set<string>();

  // Add the folder's direct children
  folderEntry.structure.forEach((item) => {
    idsToRemove.add(item.id);
    // If it's a subfolder, recursively collect its children
    if (item.type === RoomStructureItemType.Folder) {
      const nestedIds = collectFolderIdsRecursively(item.id, folderChanges);
      nestedIds.forEach((id) => idsToRemove.add(id));
    }
  });

  // Add the folder itself
  idsToRemove.add(folderId);

  // Filter out all items that should be removed
  return items.filter((item) => !idsToRemove.has(item.id));
};

/**
 * Adds a content item to a structure items array at the end. Returns the new array.
 * Reusable for both room-level and folder-level structures.
 */
export const addItemToStructure = (
  items: RoomJSONStructureItem[],
  content: Partial<ContentSchema>,
  isInlineIframeViewerEnabled = false
): RoomJSONStructureItem[] => [
  ...items,
  buildStructureItem(
    content,
    RoomStructureItemType.Content,
    isInlineIframeViewerEnabled
  ),
];

export const getCurrentItemInStructure = (
  content: Partial<ContentSchema>,
  structure: RoomJSONStructureSchema
): RoomJSONStructureItem | undefined => {
  if (!content.id || !structure?.items) {
    return;
  }

  return structure.items.find(
    (item) =>
      item.type === RoomStructureItemType.Content && item.id === content.id
  );
};

/**
 * Gets the section header id for a content, based on the structure of the room.
 */
export const getHeaderForContent = (
  content: Partial<ContentSchema>,
  structure: RoomJSONStructureSchema
): string | undefined => {
  let currentHeader;
  for (let i = 0; i < structure.items.length; i++) {
    if (structure.items[i].type === RoomStructureItemType.Header) {
      currentHeader = structure.items[i];
    }
    if (structure.items[i].id === content.id) {
      break;
    }
  }
  return currentHeader?.id;
};

export const getMentionableContents = (
  contents: ContentsSchema,
  structure: RoomJSONStructureSchema
): MentionPillType[] => {
  const [, structureContents] = matchStructureItems(contents, structure);
  return structureContents.reduce((acc, item) => {
    const [name] = getContentName(item, false);
    const content = contents.find((content) => content.id === item.id);
    if (
      isContentNotMentionable(content?.type as ContentType, content?.mtData)
    ) {
      return acc;
    }
    acc.push({
      id: item.id!,
      value: name || content?.name || 'Unknown',
      uuid: item.id!,
      denotationChar: '@',
    });
    return acc;
  }, [] as MentionPillType[]);
};
